import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@tackle-io/platform-ui';
import {
  CustomerLookupMutationVariables,
  CustomerLookupResponse,
  CustomerLookupResponseAccount,
} from 'packages/tackle-api/src/co-sell-msft/customerLookup';
import { ComponentProps, FC, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { reportError } from 'utils/monitor';
import { Drawer } from 'vendor/material';
import {
  CUSTOMER_LOOKUP_COMPANY_NAME_FIELD_NAME,
  CUSTOMER_LOOKUP_LOCATION_FIELD_NAME,
  CustomerSearchFormStep,
  MICROSOFT_CUSTOMER_SEARCH_ERROR_MESSAGE,
  TOAST_CONFIG,
} from './constants';
import type { CustomerAccountDetailsType } from './customerAccountTransformSchema';
import { CustomerAccountToSelectedCustomerFieldsSchema } from './customerAccountTransformSchema';
import { getCustomerLookupInputBody } from './helpers';
import type {
  CustomerLookupPayloadType,
  SelectedCustomerAccountType,
} from './sectionSchema';
import { CustomerLookupPayloadSchema } from './sectionSchema';

// HOOKS
import { useToast } from 'hooks';
import { useParams } from 'react-router';
import { useMsftReferralMappedFields } from '../../hooks/useMsftReferralMappedFields/useMsftReferralMappedFields';

// COMPONENTS
import { UseMutateFunction } from '@tanstack/react-query';
import FormDrawer from '../components/FormDrawer';
import CustomerAccountsList from './components/CustomerAccountsList/CustomerAccountsList';
import CustomerSearchForm from './components/CustomerSearchForm/CustomerSearchForm';
import CustomerSearchLoading from './components/CustomerSearchLoading/CustomerSearchLoading';
import LinkedAccountsList from './components/LinkedAccountsList/LinkedAccountsList';

export const AddCustomerForm = ({
  onClose,
  onSelectItem,
  customerFormStep,
  setCustomerFormStep,
  performCustomerLookup,
  customerLookupData,
  isCustomerLookupLoading,
}: {
  onClose: () => void;
  onSelectItem: (customerDetails: SelectedCustomerAccountType) => void;
  customerFormStep: CustomerSearchFormStep;
  setCustomerFormStep: (step: CustomerSearchFormStep) => void;
  performCustomerLookup: UseMutateFunction<
    CustomerLookupResponse,
    unknown,
    CustomerLookupMutationVariables,
    unknown
  >;
  customerLookupData: CustomerLookupResponse;
  isCustomerLookupLoading: boolean;
}) => {
  const { crmId } = useParams<{ crmId: string }>();
  const mappedFieldsQuery = useMsftReferralMappedFields({
    crmId,
    enabled: !!crmId,
  });
  const customerLookupInput = mappedFieldsQuery.mappedData?.customerLookupInput;
  const defaultValues = {
    companyName: customerLookupInput?.companyName || '',
    address: {
      addressLine1: customerLookupInput?.address?.addressLine1 || '',
      addressLine2: '',
      city: customerLookupInput?.address?.city || '',
      state: customerLookupInput?.address?.region || '',
      region: customerLookupInput?.address?.region || '',
      postalCode: customerLookupInput?.address?.postalCode || '',
      country: customerLookupInput?.address?.country || '',
    },
  };
  const formMethods = useForm<CustomerLookupPayloadType>({
    resolver: zodResolver(CustomerLookupPayloadSchema),
    reValidateMode: 'onChange',
    defaultValues: defaultValues,
  });
  const { toaster } = useToast();
  const handleAddToForm = (data: CustomerAccountDetailsType) => {
    onSelectItem(CustomerAccountToSelectedCustomerFieldsSchema.parse(data));
  };
  const [selectedAccount, setSelectedAccount] =
    useState<CustomerLookupResponseAccount>(null);
  const [activeTypeTabIndex, setActiveTypeTabIndex] = useState(0);

  // WATCHED FORM VALUES
  const watchedCompanyName = useWatch({
    control: formMethods.control,
    name: CUSTOMER_LOOKUP_COMPANY_NAME_FIELD_NAME,
  });

  const watchedLocationFormValue = useWatch({
    control: formMethods.control,
    name: CUSTOMER_LOOKUP_LOCATION_FIELD_NAME,
  });

  const isSubmitDisabled = !watchedCompanyName || !watchedLocationFormValue;

  const handleSearchSubmit = async () => {
    const formValues = formMethods.getValues();
    const customerLookupPayload = getCustomerLookupInputBody({ formValues });

    performCustomerLookup(
      {
        body: customerLookupPayload,
      },
      {
        onSuccess: () => {
          setCustomerFormStep(CustomerSearchFormStep.ACCOUNTS_LIST);
        },
        onError: (error) => {
          toaster({
            message: MICROSOFT_CUSTOMER_SEARCH_ERROR_MESSAGE,
            options: {
              ...TOAST_CONFIG,
              type: 'error',
            },
          });
          reportError(error);
          setCustomerFormStep(CustomerSearchFormStep.SEARCH);
        },
      },
    );
  };

  const searchFooterActions = (
    <>
      <Button appearance="primary" variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button
        appearance="primary"
        variant="contained"
        onClick={handleSearchSubmit}
        disabled={isSubmitDisabled}
      >
        Search
      </Button>
    </>
  );

  const accountsListFooterActions = (
    <>
      <Button appearance="primary" variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button
        appearance="primary"
        variant="contained"
        onClick={() => setCustomerFormStep(CustomerSearchFormStep.SEARCH)}
      >
        Back
      </Button>
    </>
  );

  const linkedAccountsFooterActions = (
    <>
      <Button appearance="primary" variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button
        appearance="primary"
        variant="contained"
        onClick={() =>
          setCustomerFormStep(CustomerSearchFormStep.ACCOUNTS_LIST)
        }
      >
        Back
      </Button>
    </>
  );

  const FOOTER_ACTIONS_BY_STEP = {
    [CustomerSearchFormStep.SEARCH]: searchFooterActions,
    [CustomerSearchFormStep.ACCOUNTS_LIST]: accountsListFooterActions,
    [CustomerSearchFormStep.LINKED_ACCOUNTS_LIST]: linkedAccountsFooterActions,
  };

  const DRAWER_TITLE_BY_STEP = {
    [CustomerSearchFormStep.SEARCH]: 'Add customer',
    [CustomerSearchFormStep.ACCOUNTS_LIST]: 'Add customer',
    [CustomerSearchFormStep.LINKED_ACCOUNTS_LIST]: 'Linked accounts',
  };

  const footerActions =
    FOOTER_ACTIONS_BY_STEP?.[customerFormStep] || searchFooterActions;

  if (isCustomerLookupLoading) {
    return (
      <FormProvider {...formMethods}>
        <FormDrawer
          title="Add customer"
          footerActions={accountsListFooterActions}
          onClose={onClose}
        >
          <CustomerSearchLoading setFormStep={setCustomerFormStep} />
        </FormDrawer>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <FormDrawer
        title={DRAWER_TITLE_BY_STEP?.[customerFormStep] || 'Add customer'}
        footerActions={footerActions}
        onClose={onClose}
      >
        {customerFormStep === CustomerSearchFormStep.SEARCH && (
          <CustomerSearchForm defaultValues={defaultValues} />
        )}

        {customerFormStep === CustomerSearchFormStep.ACCOUNTS_LIST && (
          <CustomerAccountsList
            customerSearchData={customerLookupData as CustomerLookupResponse}
            handleAddToForm={handleAddToForm}
            setFormStep={setCustomerFormStep}
            activeTypeTabIndex={activeTypeTabIndex}
            setActiveTypeTabIndex={setActiveTypeTabIndex}
            setSelectedAccount={setSelectedAccount}
          />
        )}

        {customerFormStep === CustomerSearchFormStep.LINKED_ACCOUNTS_LIST && (
          <LinkedAccountsList
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
            handleAddToForm={handleAddToForm}
            setFormStep={setCustomerFormStep}
            activeTypeTabIndex={activeTypeTabIndex}
          />
        )}
      </FormDrawer>
    </FormProvider>
  );
};

/** AddCustomerForm wrapped in a Drawer */
export const AddCustomerFormDrawer: FC<
  ComponentProps<typeof Drawer> & ComponentProps<typeof AddCustomerForm>
> = ({
  onClose,
  onSelectItem,
  open,
  customerFormStep,
  setCustomerFormStep,
  performCustomerLookup,
  customerLookupData,
  isCustomerLookupLoading,
  ...props
}) => {
  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      {...props}
      children={
        <AddCustomerForm
          onClose={onClose}
          onSelectItem={onSelectItem}
          customerFormStep={customerFormStep}
          setCustomerFormStep={setCustomerFormStep}
          performCustomerLookup={performCustomerLookup}
          customerLookupData={customerLookupData}
          isCustomerLookupLoading={isCustomerLookupLoading}
        />
      }
    />
  );
};
